import React from "react";
import { useHistory } from "react-router-dom";
import "regenerator-runtime/runtime";

import Environment from "services/environment";
import getOriginApplication from "utils/getOriginApplication";
import { trackMOEEvent } from "utils/analytics";
import { navigateHandler } from "utils/redirection";
import { generateUrl } from "utils/generateUrl";
import { handleLogOut } from "utils/logOut";

import { APPLICATIONS, LINK_TO_ROUTE } from "constants/common";

import { DROPDOWNS, MOE_HEADER } from "../constants";
import { trackAnalytics } from "services/analytics";
import Tooltip, { TOOLTIP_POSITIONS } from "components/tooltip";

import styles from "../postLoginHeader.scss";
import { enablePlatformLogin } from "utils/platformUtils";

const showLogout = getOriginApplication() === APPLICATIONS.BROWSER;

export default function ProfileCard({
  source,
  profile,
  onLogout,
  activeDropdown,
  setActiveDropdown,
  profileCompleteness,
  lobDetails,
  partyDetails,
  showProfileCompleteness,
  profilePictureUrl,
}) {
  const env = Environment.getEnv();
  const history = useHistory();
  const { personalInfo } = profile || {};
  const { userName, emailId } = personalInfo || {};

  const navigateTo = (path) => {
    const url = generateUrl(path, { lob: lobDetails?.key }, { partyId: partyDetails?.partyId });
    navigateHandler({ history, source, path: url });
  };

  const faOnLinkClick = (name) => {
    trackAnalytics({
      eVar9: `b2b|aeh|profile new|${name.toLowerCase()}`,
    });
  };

  const clearStorage = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("trackOrdertoken"); // for track order journey (Data)
    localStorage.removeItem("trackOrdercpqOrderId"); // for track order journey (Data)
    localStorage.removeItem("s-iv-user"); // for ReadOnly Super Admin journey
    sessionStorage.clear();
  };

  const onLogOutClick = () => {
    faOnLinkClick("logout");
    const isPlatformEnabled = enablePlatformLogin();
    if (isPlatformEnabled) {
      window.location.href = env.selfcareLoginPageBaseUrl + "logout";
    } else {
      handleLogOut(`${MOE_HEADER}_${DROPDOWNS.PROFILE}_LOGOUT_CLICK`, onLogout);
    }
  };

  const handleChangePasswordClick = (e) => {
    e.preventDefault();
    faOnLinkClick("change password");
    trackMOEEvent(`${MOE_HEADER}_${DROPDOWNS.PROFILE}_CHANGE_PASSWORD_CLICK`).finally(() =>
      navigateTo(LINK_TO_ROUTE.CHANGE_PASSWORD),
    );
  };

  const handleViewAllActivityClick = (e) => {
    e.preventDefault();
    faOnLinkClick("recent activity");
    trackMOEEvent(`${MOE_HEADER}_${DROPDOWNS.PROFILE}_ALL_ACTIVITY_CLICK`).finally(() => navigateTo(LINK_TO_ROUTE.USER_ACTIVITY));
  };

  const handleMyOrderClick = () => {
    faOnLinkClick("order history");
    trackMOEEvent(`${MOE_HEADER}_${DROPDOWNS.PROFILE}_TRACK_ORDER_CLICK`).finally(() => navigateTo(LINK_TO_ROUTE.TRACK_ORDER));
  };

  const handleManageAccountClick = () => {
    faOnLinkClick("user management");
    trackMOEEvent(`${MOE_HEADER}_${DROPDOWNS.PROFILE}_MANAGE_ACCOUNT_CLICK`).finally(() =>
      navigateTo(LINK_TO_ROUTE.USERS_PERMISSIONS),
    );
  };

  const profileHandler = () => {
    faOnLinkClick("my profile");
    navigateTo(LINK_TO_ROUTE.USER_PROFILE);
  };

  const profileIconHandler = () => {
    faOnLinkClick("profile icon");
    navigateTo(LINK_TO_ROUTE.USER_PROFILE);
  };

  const getInitials = (string) => {
    if (!string) return;
    let names = string.split(" ");
    let initials = names[0].substring(0, 1).toUpperCase();

    if (names.length > 1) {
      initials += names[names.length - 1].substring(0, 1).toUpperCase();
    }
    return initials;
  };

  const iconHandler = (e) => {
    e.stopPropagation();
  };

  return (
    <li
      onClick={() => setActiveDropdown(DROPDOWNS.PROFILE)}
      className={`${styles.drop_down} ${styles.user_login} ${activeDropdown === DROPDOWNS.PROFILE ? styles.active : ""}`}
    >
      <span className={activeDropdown === DROPDOWNS.PROFILE ? styles.active_icon : styles.inactive_icon}>
        <i className="aeh-icon-user-avatar"></i>
      </span>
      <div className={styles.dropbox}>
        <div className={`${styles.dropdown_card} ${styles.profile_card}`}>
          <div className={`${styles.card_header} ${styles.profile_header}`}>
            <div className={styles.profile_icon} onClick={profileIconHandler}>
              {profilePictureUrl ? (
                <img src={profilePictureUrl} alt="Profile" width={"24px"} height={"24px"} />
              ) : (
                <span className={styles.initials}>{getInitials(userName)}</span>
              )}
            </div>
            <div className={styles.profile_info}>
              <span className={styles.profile_name}>{userName}</span>
              <span className={styles.profile_email}>{emailId}</span>
            </div>
          </div>
          <ul className={styles.profile_content}>
            <li className={styles.profile_cta} onClick={profileHandler}>
              <div className={styles.profile_img}>
                <div className={styles.profile_icon}>
                  <i className="aeh-icon-my-profile"></i>
                </div>
                <div className={styles.display_name}>My Profile</div>
              </div>
              {showProfileCompleteness && (
                <div
                  className={styles.progressbar}
                  role="progressbar"
                  aria-valuenow={profileCompleteness.completenessCount}
                  aria-valuemin="0"
                  aria-valuemax="100"
                  style={{ ["--value"]: profileCompleteness.completenessCount || 0 }}
                >
                  <div className={styles.percent_used}>{`${profileCompleteness.completenessCount}%`}</div>
                </div>
              )}
            </li>
            <li className={styles.user_mgt_cta} onClick={handleManageAccountClick}>
              <div className={styles.user_mgt_img}>
                <div className={styles.info_icon}>
                  <i className="aeh-icon-user-management"></i>
                </div>
                <div className={styles.display_name}>User Management</div>
                <Tooltip
                  parent={<i className="aeh-icon-outlined-info" onClick={iconHandler}></i>}
                  content="View and manage users having access."
                  position={TOOLTIP_POSITIONS.BOTTOM_END}
                />
              </div>
              <div className={styles.new_tag}>
                <span>New</span>
              </div>
            </li>
            <li className={styles.order_hist_cta} onClick={handleMyOrderClick}>
              <div className={styles.icons}>
                <i className="aeh-icon-cart-reload"></i>
              </div>
              <div className={styles.display_name}>Order History</div>
            </li>
            <li className={styles.line}></li>
            <li className={styles.recent_act_cta} onClick={handleViewAllActivityClick}>
              <div className={styles.icons}>
                <i className="aeh-icon-recent-change"></i>
              </div>
              <div className={styles.display_name}>Recent Activity</div>
            </li>
            <li className={styles.change_pwd_cta} onClick={handleChangePasswordClick}>
              <div className={styles.icons}>
                <i className="aeh-icon-safe-banking"></i>
              </div>
              <div className={styles.display_name}>Change Password</div>
            </li>
            {showLogout && (
              <li className={styles.logout_cta} onClick={onLogOutClick}>
                <div className={styles.icons}>
                  <i className="aeh-icon-logout"></i>
                </div>
                <div className={styles.display_name}>Logout</div>
              </li>
            )}
          </ul>
        </div>
      </div>
    </li>
  );
}
